import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getIdToken } from '@/config/firebase';
import { WS_BASE_URL } from '@/config/api';
import toast from 'react-hot-toast';

// Event types
export interface ProgressUpdateEvent {
  id: string;
  type: string;
  tenantId: string;
  userId: string;
  timestamp: Date;
  data: {
    assessmentId: string;
    currentPhase: string;
    completionPercentage: number;
    details: Record<string, any>;
  };
}

export interface ConversationUpdateEvent {
  conversationId: string;
  trustLevel?: number;
  type: string;
}

export interface TypingUpdateEvent {
  userId: string;
  conversationId: string;
  isTyping: boolean;
}

// WebSocket context type
interface WebSocketContextType {
  socket: Socket | null;
  connected: boolean;
  // Typing indicators
  startTyping: (conversationId: string) => void;
  stopTyping: (conversationId: string) => void;
  // Subscriptions
  subscribeToProgress: (assessmentId: string) => void;
  subscribeToConversation: (conversationId: string) => void;
  // Event handlers
  onProgressUpdate: (callback: (event: ProgressUpdateEvent) => void) => () => void;
  onConversationUpdate: (callback: (event: ConversationUpdateEvent) => void) => () => void;
  onTypingUpdate: (callback: (event: TypingUpdateEvent) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Initialize socket connection
  useEffect(() => {
    if (!user || !userProfile) {
      // Disconnect if no user
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    // Connect to WebSocket server
    const connectSocket = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;

        const wsUrl = WS_BASE_URL;
        
                        
        const newSocket = io(wsUrl, {
          auth: {
            token,
            displayName: userProfile.displayName,
          },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          timeout: 10000, // 10 second timeout
        });

        // Connection events
        newSocket.on('connect', () => {
                              setConnected(true);
          toast.success('Real-time connection established', { id: 'ws-connected' });
        });

        newSocket.on('disconnect', (reason) => {
                    setConnected(false);
          
          // Handle different disconnect reasons
          if (reason === 'io server disconnect') {
            toast.error('Server disconnected. Attempting to reconnect...', { id: 'ws-disconnected' });
          } else if (reason === 'transport close') {
            // Transport closed is expected during reconnection
          }
        });

        newSocket.on('connect_error', (error) => {
          console.error(error);
          setConnected(false);
          
          // Only show toast for persistent errors
          if (newSocket.io.opts.reconnectionAttempts === (newSocket.io as any)._reconnectionAttempts) {
            toast.error('Unable to establish real-time connection', { id: 'ws-error' });
          }
        });
        
        newSocket.on('reconnect', (attemptNumber) => {
                    toast.success('Real-time connection restored', { id: 'ws-reconnected' });
        });

        newSocket.on('reconnect_attempt', (attemptNumber) => {
                  });

        // System notifications (kept for future use)
        newSocket.on('announcement', (data) => {
          toast(data.message, {
            icon: '📢',
            duration: 6000,
          });
        });

        socketRef.current = newSocket;
        setSocket(newSocket);
      } catch (error) {
        console.error(error);
        toast.error('Failed to establish real-time connection');
      }
    };

    connectSocket();

    // Cleanup
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnected(false);
      }
    };
  }, [user, userProfile]);

  // Typing indicators
  const startTyping = (conversationId: string) => {
    if (socket) {
      socket.emit('typing:start', conversationId);
    }
  };

  const stopTyping = (conversationId: string) => {
    if (socket) {
      socket.emit('typing:stop', conversationId);
    }
  };

  // Subscriptions
  const subscribeToProgress = (assessmentId: string) => {
    if (socket) {
      socket.emit('subscribe:progress', assessmentId);
    }
  };

  const subscribeToConversation = (conversationId: string) => {
    if (socket) {
      socket.emit('subscribe:conversation', conversationId);
    }
  };

  // Event handler helpers
  const onProgressUpdate = (callback: (event: ProgressUpdateEvent) => void) => {
    if (!socket) return () => {};
    
    socket.on('progress:update', callback);
    return () => {
      socket.off('progress:update', callback);
    };
  };

  const onConversationUpdate = (callback: (event: ConversationUpdateEvent) => void) => {
    if (!socket) return () => {};
    
    socket.on('conversation:update', callback);
    return () => {
      socket.off('conversation:update', callback);
    };
  };

  const onTypingUpdate = (callback: (event: TypingUpdateEvent) => void) => {
    if (!socket) return () => {};
    
    socket.on('typing:update', callback);
    return () => {
      socket.off('typing:update', callback);
    };
  };

  const value: WebSocketContextType = {
    socket,
    connected,
    startTyping,
    stopTyping,
    subscribeToProgress,
    subscribeToConversation,
    onProgressUpdate,
    onConversationUpdate,
    onTypingUpdate,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};